import { type FC, useState, useRef, type MutableRefObject } from 'react';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import MoreDots from 'dibs-icons/exports/legacy/MoreDots.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { useIntl, defineMessages } from 'dibs-react-intl';

import SocialDropdownItem from './components/SocialDropdownItem.tsx';
import PhotoLikeFeedbackToast from './components/PhotoLikeFeedbackToast.tsx';
import { type PhotoLikeProps } from './types.ts';
import { shareViaShareScreen } from './helpers.ts';
import { useHandlePhotoLike } from './useHandlePhotoLike.tsx';
import { getShareUri } from '../helpers/shareHelpers.ts';

import styles from './SocialDropdown.scss';

const messages = defineMessages({
    share: {
        id: 'dip.photoLiking.socialDropdown.share',
        defaultMessage: 'Share',
    },
    save: {
        id: 'dip.photoLiking.socialDropdown.save',
        defaultMessage: 'Save to Favorites',
    },
    remove: {
        id: 'dip.photoLiking.socialDropdown.remove',
        defaultMessage: 'Remove from Favorites',
    },
});

type Props = {
    photoUrl: string;
};

const SocialDropdown: FC<Props & PhotoLikeProps> = ({
    photoId,
    photoLikeId,
    photoNodeId,
    onPhotoLikeAdded,
    photoUrl,
}) => {
    const photoShareUri = getShareUri(photoUrl);
    const intl = useIntl();
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
    const { handlePhotoLike, isLiked } = useHandlePhotoLike({
        photoId,
        photoLikeId,
        photoNodeId,
        onPhotoLikeAdded,
        showFeedback: () => setIsFeedbackVisible(true),
    });
    const linkRef = useRef<HTMLButtonElement>(null);

    return (
        <>
            <div className={styles.socialDropdownWrapper}>
                <Link
                    linkRef={linkRef as MutableRefObject<HTMLButtonElement>}
                    onClick={() =>
                        setIsTooltipVisible(prevIsTooltipVisible => !prevIsTooltipVisible)
                    }
                >
                    <MoreDots className={styles.moreDotsIcon} />
                </Link>
                {/* Tooltip creates DOM element with isVisible=false */}
                {isTooltipVisible && (
                    <Tooltip
                        isVisible
                        direction="bottom"
                        type="autoWidth"
                        paddingSize="xSmall"
                        keepInViewport={false}
                        closeOnOutsideClick
                        onClose={e => {
                            //on IOS phones tooltip event listener fires before click https://github.com/1stdibs/ferrum/pull/11592
                            if (!linkRef.current?.contains(e.target as Node)) {
                                setIsTooltipVisible(false);
                            }
                        }}
                    >
                        <div className={styles.itemsContainer}>
                            <SocialDropdownItem
                                onClick={handlePhotoLike}
                                text={intl.formatMessage(isLiked ? messages.remove : messages.save)}
                            />
                            <SocialDropdownItem
                                onClick={() => shareViaShareScreen({ photoShareUri })}
                                text={intl.formatMessage(messages.share)}
                            />
                        </div>
                    </Tooltip>
                )}
            </div>
            <PhotoLikeFeedbackToast
                isToastVisible={isFeedbackVisible}
                onClose={() => setIsFeedbackVisible(false)}
            />
        </>
    );
};

export default SocialDropdown;
